<template>
  <v-card :loading="cardLoading">
    <v-card-text>
      <v-form
        ref="hourlyRatesCategoriesForm"
        v-model="valid"
        class="multi-col-validation ma-4"
        @submit.prevent="handleFormSubmit"
      >
        <v-row>
          <v-col cols="12">
            <upgrade-alert />
            <draggable
              v-model="hourlyRatesCategories"
              v-bind="dragOptions"
              :disabled="!edit"
              :scroll-sensitivity="350"
              :force-fallback="true"
              @end="onDragEnd"
              @start="onDragStart"
            >
              <div
                v-for="(hourlyRatesCategory, index) in hourlyRatesCategories"
                :id="`hourlyRatesCategoryCard_${index}`"
                :key="`${index}--hourly-rates-category`"
              >
                <v-card outlined class="my-4 card--border-primary">
                  <v-card-title>
                    <div v-if="!edit">{{ hourlyRatesCategory.name }}</div>
                    <v-text-field
                      v-else
                      v-model="hourlyRatesCategory.name"
                      outlined
                      dense
                      :label="$t('name')"
                      :placeholder="$t('name')"
                      :rules="[validators.required, validators.whiteSpaceValidator]"
                      maxlength="50"
                      hide-details="auto"
                    >
                      <template v-if="edit" #prepend>
                        <v-icon class="cursor-pointer my-handle" color="primary">{{ icons.mdiDrag }}</v-icon>
                      </template>
                      <template #append-outer>
                        <v-tooltip top transition="scroll-y-transition" open-delay="150">
                          <template #activator="{ on, attrs }">
                            <v-btn
                              color="error"
                              x-small
                              v-bind="attrs"
                              @click="removeHourlyRatesCategory(index)"
                              v-on="on"
                            >
                              <v-icon class="me-2" size="16">
                                {{ icons.mdiTrashCanOutline }}
                              </v-icon>
                              <span v-t="'delete'" />
                            </v-btn>
                          </template>
                          <span v-t="'delete'" class="text-xs" />
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-card-title>
                </v-card>
              </div>
            </draggable>
            <v-btn v-if="edit" v-t="'add'" color="primary" class="my-4" block @click="addHourlyRatesCategory" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="!edit" cols="12"
            ><v-btn
              color="primary"
              :disabled="!$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)"
              @click.prevent="edit = !edit"
            >
              <v-icon class="me-2">{{ icons.mdiPencilOutline }}</v-icon> <span v-t="'edit'" /> </v-btn
          ></v-col>
          <v-col v-if="edit" cols="12">
            <v-btn
              color="primary"
              class="me-3"
              type="submit"
              :loading="loading"
              :disabled="!valid || !(areDataChanged || areHourlyRatesCategoriesOrderChanged)"
            >
              {{ $t('save') }}
            </v-btn>
            <v-btn v-t="'discard'" outlined color="secondary" @click="restoreData" />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mdiDotsVertical, mdiTrashCanOutline, mdiDrag, mdiPencilOutline } from '@mdi/js'
import { ref, onMounted, computed, nextTick } from '@vue/composition-api'
import { required, whiteSpaceValidator } from '@core/utils/validation'
import { useActions } from 'vuex-composition-helpers'
import draggable from 'vuedraggable'
import useDraggableOptions from '@/plugins/draggable'
import { ACTIONS, PRIVATE } from '@/plugins/acl/const'
import UpgradeAlert from '@/components/UpgradeAlert.vue'

export default {
  components: {
    draggable,
    UpgradeAlert,
  },
  setup() {
    const { dragOptions, onDragStart, onDragEnd } = useDraggableOptions()
    const hourlyRatesCategoriesForm = ref(null)
    const valid = ref(true)
    const loading = ref(false)
    const cardLoading = ref(false)
    const hourlyRatesCategories = ref([])
    let frozenHourlyRatesCategories = {}

    const edit = ref(false)

    const { getProjectHourlyRatesCategories, updateProjectHourlyRatesCategories } = useActions('staff', [
      'getProjectHourlyRatesCategories',
      'updateProjectHourlyRatesCategories',
    ])

    onMounted(async () => {
      cardLoading.value = true
      hourlyRatesCategories.value = await getProjectHourlyRatesCategories()
      frozenHourlyRatesCategories = Object.freeze(_.cloneDeep(hourlyRatesCategories.value))
      cardLoading.value = false
    })

    const handleFormSubmit = async () => {
      const isFormValid = hourlyRatesCategoriesForm.value.validate()
      if (!isFormValid) return

      loading.value = true
      hourlyRatesCategories.value = await updateProjectHourlyRatesCategories(hourlyRatesCategories.value)
      frozenHourlyRatesCategories = Object.freeze(_.cloneDeep(hourlyRatesCategories.value))
      loading.value = false
      edit.value = false
    }

    const restoreData = () => {
      hourlyRatesCategories.value = _.cloneDeep(frozenHourlyRatesCategories)
      edit.value = false
    }

    const addHourlyRatesCategory = async () => {
      hourlyRatesCategories.value.push({
        name: '',
      })
      nextTick(() => {
        const el = document.getElementById(`hourlyRatesCategoryCard_${hourlyRatesCategories.value.length - 1}`)
        el.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
      })
    }

    const removeHourlyRatesCategory = (index) => {
      hourlyRatesCategories.value.splice(index, 1)
    }

    const areDataChanged = computed(() => {
      return (
        !_.isEmpty(_.differenceWith(hourlyRatesCategories.value, frozenHourlyRatesCategories, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(frozenHourlyRatesCategories, hourlyRatesCategories.value, _.isEqual))
      )
    })

    const areHourlyRatesCategoriesOrderChanged = computed(() => {
      // eslint-disable-next-line array-callback-return
      const hourlyRatesCategoriesOrder = hourlyRatesCategories.value.map((cf) => {
        if (Object.hasOwnProperty.bind(cf)('order')) return cf.order
      })

      return !hourlyRatesCategoriesOrder.every((el, index) => {
        return el === frozenHourlyRatesCategories[index]?.order
      })
    })

    return {
      handleFormSubmit,
      restoreData,
      addHourlyRatesCategory,
      removeHourlyRatesCategory,
      onDragStart,
      onDragEnd,

      areDataChanged,
      areHourlyRatesCategoriesOrderChanged,

      validators: {
        required,
        whiteSpaceValidator,
      },
      hourlyRatesCategoriesForm,
      valid,
      loading,
      cardLoading,
      hourlyRatesCategories,
      dragOptions,
      edit,
      icons: {
        mdiDotsVertical,
        mdiTrashCanOutline,
        mdiDrag,
        mdiPencilOutline,
      },
      ACTIONS,
      PRIVATE,
    }
  },
}
</script>

<style lang="scss" scoped></style>
